import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import useCurrentUserAccess from "../hooks/useCurrentUserAccess";
import BookMeSettingsPanel from "./settings/BookMeSettingsPanel";
import SubscriptionPlanSettingsCard from "./settings/SubscriptionPlanSettingsCard";
import "./Settings.css";
import "./SalesPages.css";
import "../styles/Tabs.css";

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const INACTIVITY_TIMEOUT_OPTIONS = [
  { value: 10, label: "10 Minutes" },
  { value: 30, label: "30 Minutes" },
  { value: 60, label: "1 Hour" },
  { value: 120, label: "2 Hours" }
];

const TRUSTED_DEVICE_OPTIONS = [
  { value: 1, label: "1 Day" },
  { value: 7, label: "7 Days" },
  { value: 14, label: "14 Days" },
  { value: 30, label: "30 Days" }
];

const EMAIL_INTEGRATION_OPTIONS = [
  {
    key: "outlook",
    label: "Microsoft Outlook / 365",
    description: "Send CRM emails through your connected Microsoft mailbox."
  },
  {
    key: "google",
    label: "Google / Gmail",
    description: "Send CRM emails through your connected Gmail account."
  },
  {
    key: "smtp",
    label: "Personal SMTP",
    description: "Send CRM emails through custom SMTP server settings."
  }
];

const cardFillStyle = {
  minHeight: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between"
};

function getToken() {
  return localStorage.getItem("token");
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time — America/New_York" },
  { value: "America/Chicago", label: "Central Time — America/Chicago" },
  { value: "America/Denver", label: "Mountain Time — America/Denver" },
  { value: "America/Phoenix", label: "Arizona Time — America/Phoenix" },
  { value: "America/Los_Angeles", label: "Pacific Time — America/Los_Angeles" },
  { value: "America/Anchorage", label: "Alaska Time — America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii Time — Pacific/Honolulu" },
  { value: "America/Puerto_Rico", label: "Puerto Rico — America/Puerto_Rico" }
];

function Field({ label, children, full = false }) {
  return (
    <div className={`sales-field ${full ? "sales-field-full" : ""}`}>
      {label ? <label>{label}</label> : null}
      {children}
    </div>
  );
}

function LinkCard({ title, description, to, actionLabel, variant = "lead" }) {
  return (
    <div className={`sales-section-card sales-section-${variant} settings-link-card`}>
      <div className="sales-section-head">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      {to ? (
        <Link className="settings-card-link" to={to}>
          {actionLabel}
        </Link>
      ) : null}
    </div>
  );
}

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("Account");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [npn, setNpn] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [timezone, setTimezone] = useState(getBrowserTimezone() || "America/Chicago");

  const [emailProvider, setEmailProvider] = useState("");
  const [emailProviderSaving, setEmailProviderSaving] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState("");
  const [smtpConfigured, setSmtpConfigured] = useState(false);

  const [securityLoading, setSecurityLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [navbarLogoUrl, setNavbarLogoUrl] = useState("");
  const [loginLogoUrl, setLoginLogoUrl] = useState("");
  const [accountLogoUrl, setAccountLogoUrl] = useState("");
  const [accountBusinessName, setAccountBusinessName] = useState("");

  const [appearanceLoading, setAppearanceLoading] = useState(false);
  const [appearanceSaving, setAppearanceSaving] = useState(false);
  const [navbarUploading, setNavbarUploading] = useState(false);
  const [loginUploading, setLoginUploading] = useState(false);
  const [accountLogoUploading, setAccountLogoUploading] = useState(false);

  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpHost, setSmtpHost] = useState("smtp.hostinger.com");
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("");
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [hasSmtpPassword, setHasSmtpPassword] = useState(false);

  const [healthSherpaLoading, setHealthSherpaLoading] = useState(false);
  const [healthSherpaSaving, setHealthSherpaSaving] = useState(false);
  const [healthSherpaEnabled, setHealthSherpaEnabled] = useState(false);
  const [healthSherpaBrokerUrl, setHealthSherpaBrokerUrl] = useState("");

  const [vendorIntegrationLoading, setVendorIntegrationLoading] = useState(false);
  const [vendorIntegrationError, setVendorIntegrationError] = useState("");
  const [vendorIntegration, setVendorIntegration] = useState({
    lead_post_url: "",
    client_post_url: "",
    spark_webhook_url: "",
    spark_enabled: false,
    is_active: false,
    business_name: "",
    role_key: ""
  });

  const [sessionInactivityTimeoutMinutes, setSessionInactivityTimeoutMinutes] = useState(60);
  const [trustedDeviceMfaDays, setTrustedDeviceMfaDays] = useState(1);
  const [mfaTrustEnabled, setMfaTrustEnabled] = useState(true);
  const [platformSecuritySaving, setPlatformSecuritySaving] = useState(false);

  const navbarFileInputRef = useRef(null);
  const loginFileInputRef = useRef(null);
  const accountLogoFileInputRef = useRef(null);

  const { platformRole, accountRole } = useCurrentUserAccess();

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isAccountAdmin = accountRole?.key_name === "account_admin";
  const canManageAccountAppearance = !isPlatformAdmin && isAccountAdmin;

  const timezoneOptions = useMemo(() => {
    if (
      timezone &&
      !TIMEZONE_OPTIONS.some((item) => item.value === timezone)
    ) {
      return [
        {
          value: timezone,
          label: `${timezone} — Current saved timezone`
        },
        ...TIMEZONE_OPTIONS
      ];
    }

    return TIMEZONE_OPTIONS;
  }, [timezone]);

  const tabs = useMemo(() => {
    const availableTabs = ["Account", "Billing", "Data & Customization", "Integrations", "BookMe"];

    if (isPlatformAdmin) {
      availableTabs.splice(2, 0, "Platform Settings");
    }

    return availableTabs;
  }, [isPlatformAdmin]);

  const selectedEmailIntegration = useMemo(() => {
    if (emailProvider === "google" || emailProvider === "gmail") return "google";
    if (emailProvider === "smtp") return "smtp";
    if (emailProvider === "outlook") return "outlook";
    return "outlook";
  }, [emailProvider]);

  const getProviderStatus = (providerKey) => {
    if (providerKey === "outlook") {
      if (emailProvider === "outlook" && outlookConnected) return "Connected";
      if (outlookConnected) return "Available";
      return "Not connected";
    }

    if (providerKey === "google") {
      if ((emailProvider === "google" || emailProvider === "gmail") && googleConnected) return "Connected";
      if (googleConnected) return "Available";
      return "Not connected";
    }

    if (providerKey === "smtp") {
      if (emailProvider === "smtp" && smtpConfigured) return "Connected";
      if (smtpConfigured) return "Available";
      return "Not configured";
    }

    return "";
  };

  const getProviderStatusClass = (providerKey) => {
    const status = getProviderStatus(providerKey);

    if (status === "Connected") return "connected";
    if (status === "Available") return "available";
    return "inactive";
  };

  useEffect(() => {
    const requestedTab = searchParams.get("tab");

    if (requestedTab && tabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams, tabs]);

  useEffect(() => {
    const googleStatus = searchParams.get("google");
    const message = searchParams.get("message");

    if (googleStatus === "connected") {
      alert("Google / Gmail connected successfully.");
      setSearchParams({ tab: "Integrations" });
    }

    if (googleStatus === "error") {
      alert(message || "Google / Gmail connection failed.");
      setSearchParams({ tab: "Integrations" });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const profileRes = await fetch(`${API_BASE_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (profileRes.ok) {
          const data = await profileRes.json();
          const [first, ...rest] = (data.name || "").split(" ");

          setFirstName(first || "");
          setLastName(rest.join(" ") || "");
          setEmail(data.email || "");
          setPhone(data.phone || "");
          setNpn(data.npn || "");
          setTitle(data.title || "");
          setCompany(data.company || "");
          setAddress(data.address || "");
          setCity(data.city || "");
          setState(data.state || "");
          setZip(data.zip || "");
          setTimezone(data.timezone || getBrowserTimezone() || "America/Chicago");
          setEmailProvider(data.email_provider || "");

          setSmtpFromName((prev) => prev || data.name || "");
          setSmtpFromEmail((prev) => prev || data.email || "");
          setSmtpUser((prev) => prev || data.email || "");
        }
      } catch (err) {
        console.error("Load settings error:", err.message);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchEmailProviderSettings = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/settings/email-provider`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load email integration settings");
        }

        setEmailProvider(data?.email_provider || "");
        setOutlookConnected(Boolean(data?.outlook_connected));
        setGoogleConnected(Boolean(data?.google_connected));
        setGoogleEmail(data?.google_email || "");
        setSmtpConfigured(Boolean(data?.smtp_configured));
      } catch (err) {
        console.error("Load email provider settings error:", err);
      }
    };

    fetchEmailProviderSettings();
  }, []);

  useEffect(() => {
    const fetchSmtpSettings = async () => {
      try {
        setSmtpLoading(true);

        const token = getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/settings/smtp`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load SMTP settings");
        }

        setEmailProvider(data?.email_provider || "");
        setSmtpHost(data?.smtp_host || "smtp.hostinger.com");
        setSmtpPort(Number(data?.smtp_port) || 465);
        setSmtpSecure(
          data?.smtp_secure === true ||
            data?.smtp_secure === 1 ||
            data?.smtp_secure === "1"
        );
        setSmtpUser(data?.smtp_user || "");
        setSmtpFromName(data?.smtp_from_name || "");
        setSmtpFromEmail(data?.smtp_from_email || "");
        setHasSmtpPassword(Boolean(data?.has_smtp_password));
        setSmtpConfigured(
          Boolean(
            data?.smtp_host &&
              data?.smtp_port &&
              data?.smtp_user &&
              data?.smtp_from_email &&
              data?.has_smtp_password
          )
        );
      } catch (err) {
        console.error("Load SMTP settings error:", err);
      } finally {
        setSmtpLoading(false);
      }
    };

    fetchSmtpSettings();
  }, []);

  useEffect(() => {
    const fetchHealthSherpaSettings = async () => {
      try {
        setHealthSherpaLoading(true);

        const token = getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/settings/healthsherpa`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load HealthSherpa settings");
        }

        setHealthSherpaEnabled(
          data?.healthsherpa_enabled === true ||
            data?.healthsherpa_enabled === 1 ||
            data?.healthsherpa_enabled === "1"
        );
        setHealthSherpaBrokerUrl(data?.healthsherpa_broker_url || "");
      } catch (err) {
        console.error("Load HealthSherpa settings error:", err);
      } finally {
        setHealthSherpaLoading(false);
      }
    };

    fetchHealthSherpaSettings();
  }, []);

  useEffect(() => {
    const fetchVendorIntegrationSettings = async () => {
      try {
        setVendorIntegrationLoading(true);
        setVendorIntegrationError("");

        const token = getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/settings/vendor-integration`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load lead vendor integration settings");
        }

        setVendorIntegration({
          lead_post_url: data?.lead_post_url || "",
          client_post_url: data?.client_post_url || "",
          spark_webhook_url: data?.spark_webhook_url || "",
          spark_enabled: Boolean(data?.spark_enabled),
          is_active: Boolean(data?.is_active),
          business_name: data?.business_name || "",
          role_key: data?.role_key || ""
        });
      } catch (err) {
        console.error("Load vendor integration settings error:", err);
        setVendorIntegrationError(err.message || "Failed to load lead vendor integration settings");
      } finally {
        setVendorIntegrationLoading(false);
      }
    };

    fetchVendorIntegrationSettings();
  }, []);

  useEffect(() => {
    if (!isPlatformAdmin) return;

    const fetchAppearanceAndSecurity = async () => {
      try {
        setAppearanceLoading(true);

        const res = await fetch(`${API_BASE_URL}/api/platform-settings`);
        const data = await res.json();

        setNavbarLogoUrl(data?.navbar_logo_url || "");
        setLoginLogoUrl(data?.login_logo_url || "");
        setSessionInactivityTimeoutMinutes(Number(data?.session_inactivity_timeout_minutes) || 60);
        setTrustedDeviceMfaDays(Number(data?.trusted_device_mfa_days) || 1);
        setMfaTrustEnabled(
          data?.mfa_trust_enabled === true ||
            data?.mfa_trust_enabled === 1 ||
            data?.mfa_trust_enabled === "1"
        );
      } catch (err) {
        console.error("Load platform appearance/security error:", err);
      } finally {
        setAppearanceLoading(false);
      }
    };

    fetchAppearanceAndSecurity();
  }, [isPlatformAdmin]);

  useEffect(() => {
    if (!canManageAccountAppearance) return;

    const fetchAccountAppearance = async () => {
      try {
        setAppearanceLoading(true);

        const token = getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/settings/account-appearance`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load account appearance");
        }

        setAccountBusinessName(data?.business_name || "");
        setAccountLogoUrl(data?.navbar_logo_url || data?.login_logo_url || "");
      } catch (err) {
        console.error("Load account appearance error:", err);
      } finally {
        setAppearanceLoading(false);
      }
    };

    fetchAccountAppearance();
  }, [canManageAccountAppearance]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleCopyText = async (value, label) => {
    if (!value) {
      alert(`${label} is not available yet.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      alert(`${label} copied.`);
    } catch (err) {
      console.error("Copy failed:", err);
      alert("Copy failed. Please highlight and copy the URL manually.");
    }
  };

  const handleUseBrowserTimezone = () => {
    const detectedTimezone = getBrowserTimezone();

    if (!detectedTimezone) {
      alert("Could not detect your browser timezone.");
      return;
    }

    setTimezone(detectedTimezone);
  };

  const handleToggleSparkIntegration = async (enabled) => {
    try {
      const token = getToken();

      if (!token) {
        alert("You must be logged in.");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/settings/vendor-integration/spark`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          spark_enabled: enabled
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update Spark integration.");
      }

      setVendorIntegration({
        lead_post_url: data?.lead_post_url || "",
        client_post_url: data?.client_post_url || "",
        spark_webhook_url: data?.spark_webhook_url || "",
        spark_enabled: Boolean(data?.spark_enabled),
        is_active: Boolean(data?.is_active),
        business_name: data?.business_name || "",
        role_key: data?.role_key || ""
      });

      alert(data.message || "Spark integration updated.");
    } catch (err) {
      console.error("Spark integration toggle error:", err);
      alert(err.message || "Failed to update Spark integration.");
    }
  };

  const handleProfileSave = async () => {
    try {
      const token = getToken();
      if (!token) return alert("You must be logged in.");

      const res = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          npn,
          title,
          company,
          address,
          city,
          state,
          zip,
          timezone
        })
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to save profile: ${text}`);
      }

      alert("Profile saved successfully");
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveSecurity = async () => {
    try {
      if (newPassword || confirmPassword || currentPassword) {
        if (newPassword !== confirmPassword) {
          alert("New passwords do not match.");
          return;
        }

        if (!currentPassword || !newPassword) {
          alert("Please enter your current and new passwords.");
          return;
        }

        const token = getToken();

        const passwordRes = await fetch(`${API_BASE_URL}/api/settings/password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            oldPassword: currentPassword,
            newPassword
          })
        });

        const passwordData = await passwordRes.json();

        if (!passwordRes.ok) {
          throw new Error(passwordData.error || "Failed to update password");
        }
      }

      alert("Security settings saved.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Security save error:", err);
      alert(err.message || "Failed to save security settings");
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleSaveAppearance = async () => {
    try {
      setAppearanceSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/platform-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          navbar_logo_url: navbarLogoUrl.trim() || null,
          login_logo_url: loginLogoUrl.trim() || null,
          session_inactivity_timeout_minutes: sessionInactivityTimeoutMinutes,
          trusted_device_mfa_days: trustedDeviceMfaDays,
          mfa_trust_enabled: mfaTrustEnabled
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save appearance");
      }

      setNavbarLogoUrl(data?.navbar_logo_url || "");
      setLoginLogoUrl(data?.login_logo_url || "");
      alert("Appearance updated successfully");
    } catch (err) {
      console.error("Save appearance error:", err);
      alert(err.message || "Failed to save appearance");
    } finally {
      setAppearanceSaving(false);
    }
  };

  const handleSavePlatformSecurity = async () => {
    try {
      setPlatformSecuritySaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/platform-settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          navbar_logo_url: navbarLogoUrl.trim() || null,
          login_logo_url: loginLogoUrl.trim() || null,
          session_inactivity_timeout_minutes: Number(sessionInactivityTimeoutMinutes),
          trusted_device_mfa_days: Number(trustedDeviceMfaDays),
          mfa_trust_enabled: mfaTrustEnabled
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save platform security settings");
      }

      setSessionInactivityTimeoutMinutes(Number(data?.session_inactivity_timeout_minutes) || 60);
      setTrustedDeviceMfaDays(Number(data?.trusted_device_mfa_days) || 1);
      setMfaTrustEnabled(
        data?.mfa_trust_enabled === true ||
          data?.mfa_trust_enabled === 1 ||
          data?.mfa_trust_enabled === "1"
      );

      alert("Platform security settings updated successfully");
    } catch (err) {
      console.error("Save platform security settings error:", err);
      alert(err.message || "Failed to save platform security settings");
    } finally {
      setPlatformSecuritySaving(false);
    }
  };

  const handleSaveEmailProvider = async (provider) => {
    try {
      setEmailProviderSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return false;
      }

      const res = await fetch(`${API_BASE_URL}/api/settings/email-provider`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email_provider: provider })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update email integration");
      }

      setEmailProvider(data?.email_provider || provider || "");
      return true;
    } catch (err) {
      console.error("Save email provider error:", err);
      alert(err.message || "Failed to update email integration");
      return false;
    } finally {
      setEmailProviderSaving(false);
    }
  };

  const handleSelectEmailIntegration = async (provider) => {
    setEmailProvider(provider);

    if (provider === "outlook" && outlookConnected) {
      await handleSaveEmailProvider("outlook");
      return;
    }

    if (provider === "google") {
      if (googleConnected) {
        await handleSaveEmailProvider("google");
        return;
      }

      handleConnectGoogle();
      return;
    }

    if (provider === "smtp" && smtpConfigured) {
      await handleSaveEmailProvider("smtp");
    }
  };

  const handleSaveSmtpSettings = async () => {
    try {
      setSmtpSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      if (!smtpHost.trim()) return alert("SMTP host is required.");
      if (!smtpUser.trim()) return alert("SMTP username is required.");
      if (!smtpFromEmail.trim()) return alert("From email is required.");
      if (!hasSmtpPassword && !smtpPassword.trim()) {
        return alert("SMTP password is required the first time you configure SMTP.");
      }

      const res = await fetch(`${API_BASE_URL}/api/settings/smtp`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          smtp_host: smtpHost,
          smtp_port: Number(smtpPort),
          smtp_secure: smtpSecure,
          smtp_user: smtpUser,
          smtp_password: smtpPassword,
          smtp_from_name: smtpFromName,
          smtp_from_email: smtpFromEmail
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save SMTP settings");
      }

      setEmailProvider(data?.email_provider || "smtp");
      setSmtpHost(data?.smtp_host || smtpHost);
      setSmtpPort(Number(data?.smtp_port) || smtpPort);
      setSmtpSecure(Boolean(data?.smtp_secure));
      setSmtpUser(data?.smtp_user || smtpUser);
      setSmtpFromName(data?.smtp_from_name || smtpFromName);
      setSmtpFromEmail(data?.smtp_from_email || smtpFromEmail);
      setHasSmtpPassword(Boolean(data?.has_smtp_password));
      setSmtpConfigured(true);
      setSmtpPassword("");

      alert("SMTP settings saved successfully.");
    } catch (err) {
      console.error("Save SMTP settings error:", err);
      alert(err.message || "Failed to save SMTP settings");
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleClearSmtpSettings = async () => {
    if (!window.confirm("Clear your SMTP settings?")) return;

    try {
      setSmtpSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/settings/smtp`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to clear SMTP settings");
      }

      setEmailProvider("");
      setSmtpHost("smtp.hostinger.com");
      setSmtpPort(465);
      setSmtpSecure(true);
      setSmtpUser(email || "");
      setSmtpPassword("");
      setSmtpFromName(`${firstName} ${lastName}`.trim());
      setSmtpFromEmail(email || "");
      setHasSmtpPassword(false);
      setSmtpConfigured(false);

      alert("SMTP settings cleared.");
    } catch (err) {
      console.error("Clear SMTP settings error:", err);
      alert(err.message || "Failed to clear SMTP settings.");
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleSaveHealthSherpaSettings = async () => {
    try {
      setHealthSherpaSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      if (healthSherpaEnabled && !healthSherpaBrokerUrl.trim()) {
        alert("HealthSherpa broker URL is required when enabled.");
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/settings/healthsherpa`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          healthsherpa_enabled: healthSherpaEnabled,
          healthsherpa_broker_url: healthSherpaBrokerUrl.trim()
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save HealthSherpa settings");
      }

      setHealthSherpaEnabled(Boolean(data?.healthsherpa_enabled));
      setHealthSherpaBrokerUrl(data?.healthsherpa_broker_url || "");

      alert("HealthSherpa settings saved successfully.");
    } catch (err) {
      console.error("Save HealthSherpa settings error:", err);
      alert(err.message || "Failed to save HealthSherpa settings");
    } finally {
      setHealthSherpaSaving(false);
    }
  };

  const uploadImageToCloudinary = async (file) => {
    if (!file.type.startsWith("image/")) throw new Error("Please choose an image file.");

    if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
      throw new Error("Cloudinary upload settings are missing.");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("folder", "agent-book-crm/branding");

    const uploadRes = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: "POST", body: formData }
    );

    const uploadData = await uploadRes.json();

    if (!uploadRes.ok) {
      throw new Error(uploadData?.error?.message || "Image upload failed");
    }

    return uploadData.secure_url || "";
  };

  const handleUploadLogo = async ({ event, setUploading, setLogoUrl, inputRef, label }) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const uploadedUrl = await uploadImageToCloudinary(file);
      setLogoUrl(uploadedUrl);
    } catch (err) {
      console.error(`${label} upload error:`, err);
      alert(err.message || `Failed to upload ${label}`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleSaveAccountAppearance = async () => {
    try {
      setAppearanceSaving(true);

      const token = getToken();
      if (!token) {
        alert("You must be logged in.");
        return;
      }

      const cleanLogoUrl = accountLogoUrl.trim() || null;

      const res = await fetch(`${API_BASE_URL}/api/settings/account-appearance`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          navbar_logo_url: cleanLogoUrl,
          login_logo_url: cleanLogoUrl
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save account appearance");
      }

      setAccountBusinessName(data?.business_name || accountBusinessName);
      setAccountLogoUrl(data?.navbar_logo_url || data?.login_logo_url || "");

      alert("Account appearance updated successfully");
    } catch (err) {
      console.error("Save account appearance error:", err);
      alert(err.message || "Failed to save account appearance");
    } finally {
      setAppearanceSaving(false);
    }
  };

  const handleDisconnectOutlook = async () => {
    if (!window.confirm("Disconnect Outlook account?")) return;

    try {
      const token = getToken();

      const res = await fetch(`${API_BASE_URL}/api/connect/outlook/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        alert("Outlook disconnected.");
        setEmailProvider("");
        setOutlookConnected(false);
      } else {
        alert("Failed to disconnect Outlook.");
      }
    } catch (err) {
      console.error("Disconnect error:", err);
    }
  };

  const handleConnectOutlook = () => {
    const token = getToken();

    if (!token) {
      alert("You must be logged in.");
      return;
    }

    window.location.href = `${API_BASE_URL}/api/emails/connect/outlook?token=${token}`;
  };

  const handleConnectGoogle = () => {
    const token = getToken();

    if (!token) {
      alert("You must be logged in.");
      return;
    }

    const returnUrl = `${window.location.origin}/settings?tab=Integrations`;

    window.location.href =
      `${API_BASE_URL}/api/connect/google?token=${encodeURIComponent(token)}&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google / Gmail account?")) return;

    try {
      const token = getToken();

      const res = await fetch(`${API_BASE_URL}/api/connect/google/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to disconnect Google / Gmail.");
      }

      alert("Google / Gmail disconnected.");
      setEmailProvider("");
      setGoogleConnected(false);
      setGoogleEmail("");
    } catch (err) {
      console.error("Google disconnect error:", err);
      alert(err.message || "Failed to disconnect Google / Gmail.");
    }
  };

  const renderAccountTab = () => (
    <div className="settings-modern-stack">
      <div className="sales-profile-grid">
        <div className="sales-section-card sales-section-lead">
          <div className="sales-section-head">
            <h2>Profile</h2>
            <p>Manage your personal account information and contact details.</p>
          </div>

          <div className="sales-form-fields">
            <Field label="First Name"><input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Last Name"><input value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
            <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
            <Field label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
            <Field label="NPN"><input value={npn} onChange={(e) => setNpn(e.target.value)} /></Field>
            <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Company"><input value={company} onChange={(e) => setCompany(e.target.value)} /></Field>
            <Field label="Address" full><input value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
            <Field label="City"><input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
            <Field label="State"><input value={state} onChange={(e) => setState(e.target.value)} /></Field>
            <Field label="Zip"><input value={zip} onChange={(e) => setZip(e.target.value)} /></Field>
            <Field label="Calendar / Scheduling Timezone" full>
              <div className="settings-inline-actions">
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  {timezoneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <button type="button" className="sales-secondary-btn" onClick={handleUseBrowserTimezone}>
                  Use Mine
                </button>
              </div>
              <p className="settings-muted-text">
                This timezone is used for your CRM calendar, task schedule, and BookMe availability.
              </p>
            </Field>
          </div>

          <div style={{ marginTop: "20px" }}>
            <button className="sales-primary-btn" onClick={handleProfileSave}>
              Save Profile
            </button>
          </div>
        </div>

        <div className="sales-section-card sales-section-client">
          <div className="sales-section-head">
            <h2>Security</h2>
            <p>Update your account password.</p>
          </div>

          <div className="sales-form-fields">
            <Field label="Current Password" full>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Field>

            <Field label="New Password">
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>

            <Field label="Confirm New Password">
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Field>
          </div>

          <div style={{ marginTop: "20px" }}>
            <button className="sales-secondary-btn" onClick={handleSaveSecurity} disabled={securityLoading}>
              {securityLoading ? "Saving..." : "Update Security"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="settings-modern-stack">
      <SubscriptionPlanSettingsCard />
    </div>
  );

  const renderAppearanceTab = () => {
    if (!canManageAccountAppearance) {
      return null;
    }

    return (
      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Account Branding</h2>
          <p>Upload the logo shown for your agency in the CRM navigation and login screens.</p>
        </div>

        {accountBusinessName && (
          <p className="settings-muted-text" style={{ marginTop: 0 }}>
            Account: <strong>{accountBusinessName}</strong>
          </p>
        )}

        <div className="sales-form-fields">
          <Field label="Agency Logo URL" full>
            <input
              type="text"
              value={accountLogoUrl}
              onChange={(e) => setAccountLogoUrl(e.target.value)}
              placeholder="https://..."
            />
          </Field>

          <Field label="Upload Agency Logo" full>
            <div className="settings-inline-actions">
              <input
                ref={accountLogoFileInputRef}
                type="file"
                accept="image/*"
                onChange={(event) =>
                  handleUploadLogo({
                    event,
                    setUploading: setAccountLogoUploading,
                    setLogoUrl: setAccountLogoUrl,
                    inputRef: accountLogoFileInputRef,
                    label: "agency logo"
                  })
                }
              />

              <button
                type="button"
                className="sales-secondary-btn"
                onClick={() => accountLogoFileInputRef.current?.click()}
                disabled={accountLogoUploading}
              >
                {accountLogoUploading ? "Uploading..." : "Choose File"}
              </button>
            </div>
            <p className="settings-muted-text">
              Recommended: transparent PNG or SVG with a horizontal layout.
            </p>
          </Field>
        </div>

        {accountLogoUrl && (
          <div style={{ marginTop: "16px" }}>
            <p className="settings-muted-text">Preview</p>
            <div
              style={{
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                borderRadius: "12px",
                padding: "16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "180px",
                minHeight: "80px"
              }}
            >
              <img
                src={accountLogoUrl}
                alt="Agency logo preview"
                style={{
                  maxWidth: "220px",
                  maxHeight: "90px",
                  objectFit: "contain"
                }}
              />
            </div>
          </div>
        )}

        <div className="settings-inline-actions" style={{ marginTop: "18px" }}>
          <button className="sales-primary-btn" onClick={handleSaveAccountAppearance} disabled={appearanceSaving}>
            {appearanceSaving ? "Saving..." : "Save Branding"}
          </button>
        </div>
      </div>
    );
  };

  const renderPlatformSettingsTab = () => {
    if (!isPlatformAdmin) {
      return null;
    }

    return (
      <div className="settings-modern-stack">
        <div className="card-grid settings-modern-grid">
          <LinkCard title="Subscription Plans" description="Create plans, pricing, Stripe price IDs, feature access, and user limits." to="/settings/subscription-plans" actionLabel="Manage Subscription Plans" />
          <LinkCard title="System Emails" description="Manage transactional templates for activation, billing, onboarding, and account notifications." to="/settings/system-emails" actionLabel="Manage System Emails" />
        </div>

        <div className="sales-profile-grid">
          <div className="sales-section-card sales-section-subscription" style={cardFillStyle}>
            <div className="sales-section-head">
              <h2>Platform Appearance</h2>
              <p>Control shared logos and branding for Agent Book CRM.</p>
            </div>

            <div className="sales-form-fields">
              <Field label="Navbar Logo URL" full>
                <input
                  type="text"
                  value={navbarLogoUrl}
                  onChange={(e) => setNavbarLogoUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Field>

              <Field label="Upload Navbar Logo" full>
                <div className="settings-inline-actions">
                  <input
                    ref={navbarFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleUploadLogo({
                        event,
                        setUploading: setNavbarUploading,
                        setLogoUrl: setNavbarLogoUrl,
                        inputRef: navbarFileInputRef,
                        label: "navbar logo"
                      })
                    }
                  />

                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={() => navbarFileInputRef.current?.click()}
                    disabled={navbarUploading}
                  >
                    {navbarUploading ? "Uploading..." : "Choose File"}
                  </button>
                </div>
              </Field>

              <Field label="Login Logo URL" full>
                <input
                  type="text"
                  value={loginLogoUrl}
                  onChange={(e) => setLoginLogoUrl(e.target.value)}
                  placeholder="https://..."
                />
              </Field>

              <Field label="Upload Login Logo" full>
                <div className="settings-inline-actions">
                  <input
                    ref={loginFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleUploadLogo({
                        event,
                        setUploading: setLoginUploading,
                        setLogoUrl: setLoginLogoUrl,
                        inputRef: loginFileInputRef,
                        label: "login logo"
                      })
                    }
                  />

                  <button
                    type="button"
                    className="sales-secondary-btn"
                    onClick={() => loginFileInputRef.current?.click()}
                    disabled={loginUploading}
                  >
                    {loginUploading ? "Uploading..." : "Choose File"}
                  </button>
                </div>
              </Field>
            </div>

            <div style={{ marginTop: "20px" }}>
              <button className="sales-primary-btn" onClick={handleSaveAppearance} disabled={appearanceSaving}>
                {appearanceSaving ? "Saving..." : "Save Appearance"}
              </button>
            </div>
          </div>

          <div className="sales-section-card sales-section-client" style={cardFillStyle}>
            <div className="sales-section-head">
              <h2>Platform Security</h2>
              <p>Set default inactivity timeout and MFA trusted device duration.</p>
            </div>

            <div className="sales-form-fields">
              <Field label="Inactivity Timeout">
                <select
                  value={sessionInactivityTimeoutMinutes}
                  onChange={(e) => setSessionInactivityTimeoutMinutes(Number(e.target.value))}
                >
                  {INACTIVITY_TIMEOUT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Trusted Device MFA Duration">
                <select
                  value={trustedDeviceMfaDays}
                  onChange={(e) => setTrustedDeviceMfaDays(Number(e.target.value))}
                >
                  {TRUSTED_DEVICE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Remember Trusted Devices" full>
                <label className="settings-toggle-row">
                  <input
                    type="checkbox"
                    checked={mfaTrustEnabled}
                    onChange={(e) => setMfaTrustEnabled(e.target.checked)}
                  />
                  <span>Allow users to trust devices after MFA verification.</span>
                </label>
              </Field>
            </div>

            <div style={{ marginTop: "20px" }}>
              <button className="sales-primary-btn" onClick={handleSavePlatformSecurity} disabled={platformSecuritySaving}>
                {platformSecuritySaving ? "Saving..." : "Save Security"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDataCustomizationTab = () => (
  <div className="settings-modern-stack">
    {renderAppearanceTab()}

    <div className="card-grid settings-modern-grid">
      {isPlatformAdmin ? (
        <LinkCard
          title="Carrier Catalog"
          description="Manage the platform master list of carriers available throughout the CRM."
          to="/settings/carriers"
          actionLabel="Manage Carrier Catalog"
        />
      ) : (
        <LinkCard
          title="Carriers"
          description="Choose which carriers your agency offers for each coverage type."
          to="/settings/carriers"
          actionLabel="Manage Carriers"
        />
      )}

      <LinkCard
        title="Import Leads / Clients"
        description="Upload CSV files to preview and import leads, clients, and optional policy information."
        to="/settings/imports"
        actionLabel="Import Leads / Clients"
      />

      <LinkCard
        title="Custom Fields"
        description="Create account-specific fields for leads, clients, policies, and opportunities."
        to="/settings/custom-fields"
        actionLabel="Manage Custom Fields"
      />

      <LinkCard
        title="Coverage Types"
        description="Manage the coverage categories and plan-type options used throughout the CRM."
        to="/settings/coverage-types"
        actionLabel="Manage Coverage Types"
      />

      <LinkCard
        title="Policy Statuses"
        description="Customize policy status values used for active, pending, cancelled, and other policy stages."
        to="/settings/policy-statuses"
        actionLabel="Manage Policy Statuses"
      />
    </div>
  </div>
);

  const renderIntegrationsTab = () => (
    <div className="settings-modern-stack">
      <div className="sales-section-card sales-section-client">
        <div className="sales-section-head">
          <h2>Email Sending Provider</h2>
          <p>Choose how CRM emails are sent from your account.</p>
        </div>

        <div className="settings-integration-grid">
          {EMAIL_INTEGRATION_OPTIONS.map((provider) => (
            <button
              key={provider.key}
              type="button"
              className={`settings-integration-card ${
                selectedEmailIntegration === provider.key ? "active" : ""
              }`}
              onClick={() => handleSelectEmailIntegration(provider.key)}
              disabled={emailProviderSaving}
            >
              <div>
                <h3>{provider.label}</h3>
                <p>{provider.description}</p>
              </div>
              <span className={`settings-integration-status ${getProviderStatusClass(provider.key)}`}>
                {getProviderStatus(provider.key)}
              </span>
            </button>
          ))}
        </div>

        <div className="settings-inline-actions" style={{ marginTop: "20px" }}>
          {selectedEmailIntegration === "outlook" && !outlookConnected && (
            <button className="sales-primary-btn" onClick={handleConnectOutlook}>
              Connect Outlook
            </button>
          )}

          {selectedEmailIntegration === "outlook" && outlookConnected && (
            <button className="sales-secondary-btn" onClick={handleDisconnectOutlook}>
              Disconnect Outlook
            </button>
          )}

          {selectedEmailIntegration === "google" && !googleConnected && (
            <button className="sales-primary-btn" onClick={handleConnectGoogle}>
              Connect Google / Gmail
            </button>
          )}

          {selectedEmailIntegration === "google" && googleConnected && (
            <button className="sales-secondary-btn" onClick={handleDisconnectGoogle}>
              Disconnect Google / Gmail
            </button>
          )}
        </div>

        <p className="settings-muted-text" style={{ marginTop: "14px" }}>
          The selected provider becomes the default sender for email templates and CRM outreach.
        </p>
      </div>

      {selectedEmailIntegration === "smtp" && (
        <div className="sales-section-card sales-section-subscription">
          <div className="sales-section-head">
            <h2>SMTP Settings</h2>
            <p>Configure your personal SMTP account for CRM email delivery.</p>
          </div>

          <div className="sales-form-fields">
            <Field label="SMTP Host">
              <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
            </Field>

            <Field label="SMTP Port">
              <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />
            </Field>

            <Field label="Secure Connection">
              <select value={smtpSecure ? "true" : "false"} onChange={(e) => setSmtpSecure(e.target.value === "true")}>
                <option value="true">SSL / TLS</option>
                <option value="false">STARTTLS / Non-secure</option>
              </select>
            </Field>

            <Field label="SMTP Username">
              <input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
            </Field>

            <Field label={hasSmtpPassword ? "New SMTP Password (optional)" : "SMTP Password"}>
              <input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} />
            </Field>

            <Field label="From Name">
              <input value={smtpFromName} onChange={(e) => setSmtpFromName(e.target.value)} />
            </Field>

            <Field label="From Email">
              <input value={smtpFromEmail} onChange={(e) => setSmtpFromEmail(e.target.value)} />
            </Field>
          </div>

          <div className="settings-inline-actions" style={{ marginTop: "20px" }}>
            <button className="sales-primary-btn" onClick={handleSaveSmtpSettings} disabled={smtpSaving}>
              {smtpSaving ? "Saving..." : "Save SMTP Settings"}
            </button>

            {smtpConfigured && (
              <button className="sales-secondary-btn" onClick={handleClearSmtpSettings} disabled={smtpSaving}>
                Clear SMTP
              </button>
            )}
          </div>
        </div>
      )}

      <div className="sales-section-card sales-section-lead">
        <div className="sales-section-head">
          <h2>Quote & Enrollment Integrations</h2>
          <p>Manage external enrollment tools connected to this CRM account.</p>
        </div>

        <div className="sales-form-fields">
          <Field label="Enable HealthSherpa" full>
            <label className="settings-toggle-row">
              <input
                type="checkbox"
                checked={healthSherpaEnabled}
                onChange={(e) => setHealthSherpaEnabled(e.target.checked)}
              />
              <span>Show HealthSherpa launch options in ACA workflows.</span>
            </label>
          </Field>

          <Field label="HealthSherpa Broker URL" full>
            <input
              type="text"
              value={healthSherpaBrokerUrl}
              onChange={(e) => setHealthSherpaBrokerUrl(e.target.value)}
              placeholder="https://www.healthsherpa.com/?_agent_id=..."
            />
          </Field>
        </div>

        <div className="settings-inline-actions" style={{ marginTop: "20px" }}>
          <button className="sales-primary-btn" onClick={handleSaveHealthSherpaSettings} disabled={healthSherpaSaving || healthSherpaLoading}>
            {healthSherpaSaving ? "Saving..." : "Save HealthSherpa"}
          </button>
        </div>
      </div>

      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Lead Vendor Integration</h2>
          <p>Use these account-specific endpoints to connect external lead vendors and automation platforms.</p>
        </div>

        {vendorIntegrationError && (
          <div className="settings-warning-card" style={{ marginBottom: "16px" }}>
            {vendorIntegrationError}
          </div>
        )}

        <div className="sales-form-fields">
          <Field label="Lead POST URL" full>
            <div className="settings-copy-row">
              <input value={vendorIntegration.lead_post_url} readOnly />
              <button type="button" className="sales-secondary-btn" onClick={() => handleCopyText(vendorIntegration.lead_post_url, "Lead POST URL")}>
                Copy
              </button>
            </div>
          </Field>

          <Field label="Client POST URL" full>
            <div className="settings-copy-row">
              <input value={vendorIntegration.client_post_url} readOnly />
              <button type="button" className="sales-secondary-btn" onClick={() => handleCopyText(vendorIntegration.client_post_url, "Client POST URL")}>
                Copy
              </button>
            </div>
          </Field>

          <Field label="Spark Webhook URL" full>
            <div className="settings-copy-row">
              <input value={vendorIntegration.spark_webhook_url} readOnly />
              <button type="button" className="sales-secondary-btn" onClick={() => handleCopyText(vendorIntegration.spark_webhook_url, "Spark Webhook URL")}>
                Copy
              </button>
            </div>
          </Field>

          <Field label="Spark Delivery" full>
            <label className="settings-toggle-row">
              <input
                type="checkbox"
                checked={vendorIntegration.spark_enabled}
                onChange={(e) => handleToggleSparkIntegration(e.target.checked)}
                disabled={vendorIntegrationLoading}
              />
              <span>Forward inbound leads to Spark using this account's webhook URL.</span>
            </label>
          </Field>
        </div>

        <p className="settings-muted-text" style={{ marginTop: "12px" }}>
          These URLs are generated from your subscriber account. Keep them private and share only with trusted vendors.
        </p>
      </div>
    </div>
  );

  const renderCurrentTab = () => {
    switch (activeTab) {
      case "Account":
        return renderAccountTab();
      case "Billing":
        return renderBillingTab();
      case "Platform Settings":
        return renderPlatformSettingsTab();
      case "Data & Customization":
        return renderDataCustomizationTab();
      case "Integrations":
        return renderIntegrationsTab();
      case "BookMe":
        return <BookMeSettingsPanel />;
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content settings-page-modern">
        <div className="settings-hero">
          <div>
            <p className="settings-eyebrow">Settings</p>
            <h1>Account Settings</h1>
            <p>Manage profile, billing, integrations, branding, and CRM customization options.</p>
          </div>
        </div>

        <div className="tabs">
          {tabs.map((tab) => (
            <div
              key={tab}
              className={`tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabClick(tab)}
            >
              {tab}
            </div>
          ))}
        </div>

        <div className="tab-content settings-tab-modern">
          {renderCurrentTab()}
        </div>
      </div>
    </>
  );
};

export default Settings;